/**
 * Domain Layer - EditableFieldsPolicy
 * 編集可能フィールドポリシー
 */

import { getAllStatusLabels } from './ReservationStatus';

/**
 * セレクトオプション型
 */
export interface SelectOption {
  /** 値 */
  readonly value: string;
  /** 表示ラベル */
  readonly label: string;
}

/**
 * 編集可能フィールドの種類
 */
export const EditableField = {
  /** ステータス */
  STATUS: 'status',
  /** 担当医師 */
  DOCTOR: 'doctor',
} as const;

export type EditableField = typeof EditableField[keyof typeof EditableField];

/**
 * 編集可能フィールド一覧
 */
export const EDITABLE_FIELDS: readonly EditableField[] = [
  EditableField.STATUS,
  EditableField.DOCTOR,
] as const;

/**
 * ステータス選択オプション
 */
export const STATUS_OPTIONS: readonly SelectOption[] = getAllStatusLabels().map(
  (label) => ({
    value: label,
    label,
  })
);

/**
 * 医師選択オプション
 * NOTE: 実際の医師リストはAPIから取得するか、設定で管理する
 */
export const DOCTOR_OPTIONS: readonly SelectOption[] = [
  { value: 'テスト先生', label: 'テスト先生' },
] as const;

/**
 * 手動入力オプションの値
 */
export const MANUAL_INPUT_VALUE = 'manual_input' as const;

/**
 * 手動入力オプション
 */
export const MANUAL_INPUT_OPTION: SelectOption = {
  value: MANUAL_INPUT_VALUE,
  label: '手動入力...',
} as const;

/**
 * ステータス選択オプション（手動入力オプション付き）
 */
export const getStatusOptionsWithManualInput = (): SelectOption[] => [
  ...STATUS_OPTIONS,
  MANUAL_INPUT_OPTION,
];

/**
 * 医師選択オプション（手動入力オプション付き）
 */
export const getDoctorOptionsWithManualInput = (): SelectOption[] => [
  ...DOCTOR_OPTIONS,
  MANUAL_INPUT_OPTION,
];

/**
 * フィールドが編集可能かどうかを判定する
 * @param field フィールド名
 * @returns 編集可能な場合true
 */
export const isEditableField = (field: string): field is EditableField => {
  return EDITABLE_FIELDS.includes(field as EditableField);
};

/**
 * フィールドの選択オプションを取得する
 * @param field 編集可能フィールド
 * @param includeManualInput 手動入力オプションを含めるか
 * @returns 選択オプションの配列
 */
export const getFieldOptions = (
  field: EditableField,
  includeManualInput: boolean = true
): SelectOption[] => {
  switch (field) {
    case EditableField.STATUS:
      return includeManualInput
        ? getStatusOptionsWithManualInput()
        : [...STATUS_OPTIONS];
    case EditableField.DOCTOR:
      return includeManualInput
        ? getDoctorOptionsWithManualInput()
        : [...DOCTOR_OPTIONS];
    default:
      return [];
  }
};

/**
 * 値が手動入力オプションかどうかを判定する
 * @param value 値
 * @returns 手動入力オプションの場合true
 */
export const isManualInputValue = (value: string): boolean => {
  return value === MANUAL_INPUT_VALUE;
};

/**
 * フィールドの日本語ラベルを取得する
 * @param field 編集可能フィールド
 * @returns 日本語ラベル
 */
export const getFieldLabel = (field: EditableField): string => {
  switch (field) {
    case EditableField.STATUS:
      return 'ステータス';
    case EditableField.DOCTOR:
      return '担当医師';
    default:
      return field;
  }
};
